import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'

/**
 * The contract refuses an expiry less than one hour ahead (MIN_EXPIRY_LEAD).
 * The picker offers a few minutes more than that, so a choice that was legal
 * when it was made is still legal after the wallet has been opened, read and
 * signed. Anything below the resulting floor is not flagged — it cannot be
 * clicked at all, which is the point: the validation is expressed as what the
 * calendar lets you reach, not as a message after the fact.
 */
const MIN_LEAD_MS = 3600_000
const SIGNING_HEADROOM_MS = 5 * 60_000

/** Quarter hours: enough precision for an expiry, and four options instead of sixty. */
const MINUTE_STEPS = [0, 15, 30, 45]

const VALUE_FORMAT = 'YYYY-MM-DDTHH:mm'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const Trigger = styled.button<{ $empty: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme, $empty }) => ($empty ? theme.colors.gray6 : theme.colors.white)};
  font-size: 14px;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  outline: none;
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.gray6};
  }
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ClearButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.gray6};
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
  &:hover {
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.gray9};
  }
`

const Panel = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  min-width: 288px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.backgroundContrast};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
`

const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Preset = styled.button`
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: transparent;
  color: ${({ theme }) => theme.colors.white2};
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
    color: ${({ theme }) => theme.colors.white};
  }
`

const MonthRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.purple};
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`

const WeekdayCell = styled.div`
  padding: 4px 0;
  text-align: center;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gray6};
`

const DayCell = styled.button<{
  $selected: boolean
  $today: boolean
  $outside: boolean
}>`
  aspect-ratio: 1 / 1;
  min-height: 32px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $selected, $today }) => {
      if ($selected) return theme.colors.purple
      if ($today) return theme.colors.gray8
      return 'transparent'
    }};
  background: ${({ theme, $selected }) => ($selected ? `${theme.colors.purple}33` : 'transparent')};
  color: ${({ theme, $selected, $outside }) => {
    if ($selected) return theme.colors.white
    if ($outside) return theme.colors.gray7
    return theme.colors.white2
  }};
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.purple};
    color: ${({ theme }) => theme.colors.white};
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.gray7};
    cursor: not-allowed;
    border-color: transparent;
    background: transparent;
  }
`

const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TimeSelect = styled.select`
  flex: 1;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-family: inherit;
  font-size: 14px;
  outline: none;
  cursor: pointer;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.gray9};
`

const TextButton = styled.button<{ $accent?: boolean }>`
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme, $accent }) => ($accent ? theme.colors.purple : theme.colors.gray6)};
  font-family: inherit;
  font-size: 13px;
  font-weight: ${({ $accent }) => ($accent ? 700 : 400)};
  cursor: pointer;
  &:hover {
    color: ${({ theme, $accent }) => ($accent ? theme.colors.purpleHover : theme.colors.white)};
  }
`

/** Monday-first weekday labels, from the browser's own locale data. */
const WEEKDAYS = Array.from({ length: 7 }, (_, i) =>
  dayjs()
    .startOf('week')
    .add(i + 1, 'day')
    .format('dd')
)

interface ExpiryPickerProps {
  /** `YYYY-MM-DDTHH:mm`, or '' for "never expires" — the datetime-local shape. */
  value: string
  onChange: (next: string) => void
  disabled?: boolean
}

/**
 * Choosing when a listing or an offer ends. Replaces the browser's
 * `datetime-local`, which renders differently in every browser, offers no
 * sense of what is too soon, and invites a typed date that the contract then
 * refuses. Here the floor is enforced by the calendar itself: earlier days
 * and earlier quarter-hours are disabled, and the three presets cover the
 * cases people actually want without touching the grid at all.
 */
const ExpiryPicker: React.FC<ExpiryPickerProps> = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const selected = useMemo(() => (value ? dayjs(value) : null), [value])

  /**
   * The floor moves with the clock, so it is state rather than a constant:
   * a panel left open past its own earliest slot must stop offering it.
   */
  /**
   * Truncated to the minute, and that is load-bearing rather than tidiness:
   * the values this picker produces carry no seconds, so a floor holding
   * 13:30:45 would rule 13:30 too early — disabling the very slot the picker
   * had just chosen. The lost 59 seconds come out of the signing headroom,
   * which is minutes wide.
   */
  const nextFloor = () =>
    dayjs()
      .add(MIN_LEAD_MS + SIGNING_HEADROOM_MS, 'millisecond')
      .second(0)
      .millisecond(0)

  const [floor, setFloor] = useState<Dayjs>(nextFloor)
  useEffect(() => {
    if (!open) return undefined
    setFloor(nextFloor())
    const h = setInterval(() => setFloor(nextFloor()), 30_000)
    return () => clearInterval(h)
  }, [open])

  const [viewMonth, setViewMonth] = useState<Dayjs>(() => (selected ?? dayjs()).startOf('month'))
  useEffect(() => {
    if (open) setViewMonth((selected ?? dayjs()).startOf('month'))
  }, [open, selected])

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const commit = useCallback((next: Dayjs) => onChange(next.format(VALUE_FORMAT)), [onChange])

  /** A preset lands on a clean quarter hour — nobody means "in 24 hours and 37 seconds". */
  const applyPreset = useCallback(
    (days: number) => {
      const raw = dayjs().add(days, 'day')
      const rounded = raw
        .minute(Math.ceil(raw.minute() / 15) * 15)
        .second(0)
        .millisecond(0)
      commit(rounded)
      setOpen(false)
    },
    [commit]
  )

  const days = useMemo(() => {
    // dayjs weeks start on Sunday; step back to the Monday on or before the 1st.
    const monday = viewMonth.subtract((viewMonth.day() + 6) % 7, 'day')
    return Array.from({ length: 42 }, (_, i) => monday.add(i, 'day'))
  }, [viewMonth])

  const floorDay = floor.startOf('day')
  const canGoBack = viewMonth.isAfter(floorDay, 'month')

  const pickDay = useCallback(
    (day: Dayjs) => {
      // Keep the time already chosen where it is still legal; otherwise take
      // the first quarter-hour this day allows.
      const keep = selected ?? floor
      let next = day.hour(keep.hour()).minute(keep.minute()).second(0).millisecond(0)
      if (next.isBefore(floor)) {
        const m = Math.ceil(floor.minute() / 15) * 15
        next = floor.minute(0).add(m, 'minute').second(0).millisecond(0)
      }
      commit(next)
    },
    [commit, floor, selected]
  )

  const setHour = useCallback(
    (hour: number) => {
      if (!selected) return
      commit(selected.hour(hour))
    },
    [commit, selected]
  )

  const setMinute = useCallback(
    (minute: number) => {
      if (!selected) return
      commit(selected.minute(minute))
    },
    [commit, selected]
  )

  const hourDisabled = useCallback(
    (hour: number) => Boolean(selected && selected.hour(hour).minute(59).isBefore(floor)),
    [floor, selected]
  )
  const minuteDisabled = useCallback(
    (minute: number) => Boolean(selected && selected.minute(minute).isBefore(floor)),
    [floor, selected]
  )

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        type="button"
        $empty={!selected}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span>{selected ? selected.format('ddd, D MMM YYYY · HH:mm') : t('marketplace.expiryNever')}</span>
        {selected ? (
          <ClearButton
            role="button"
            aria-label={t('marketplace.expiryClear')}
            onClick={(e) => {
              e.stopPropagation()
              onChange('')
              setOpen(false)
            }}
          >
            ×
          </ClearButton>
        ) : (
          <span aria-hidden>▾</span>
        )}
      </Trigger>

      {open && (
        <Panel role="dialog">
          <PresetRow>
            <Preset type="button" onClick={() => applyPreset(1)}>
              {t('marketplace.expiryIn24h')}
            </Preset>
            <Preset type="button" onClick={() => applyPreset(7)}>
              {t('marketplace.expiryIn7d')}
            </Preset>
            <Preset type="button" onClick={() => applyPreset(30)}>
              {t('marketplace.expiryIn30d')}
            </Preset>
          </PresetRow>

          <MonthRow>
            <NavButton
              type="button"
              disabled={!canGoBack}
              aria-label={t('marketplace.expiryPrevMonth')}
              onClick={() => setViewMonth((m) => m.subtract(1, 'month'))}
            >
              ‹
            </NavButton>
            <Text variant={TextVariant.BODY_SMALL_BOLD}>{viewMonth.format('MMMM YYYY')}</Text>
            <NavButton
              type="button"
              aria-label={t('marketplace.expiryNextMonth')}
              onClick={() => setViewMonth((m) => m.add(1, 'month'))}
            >
              ›
            </NavButton>
          </MonthRow>

          <Grid>
            {WEEKDAYS.map((w) => (
              <WeekdayCell key={w}>{w}</WeekdayCell>
            ))}
            {days.map((day) => {
              const tooEarly = day.isBefore(floorDay, 'day')
              return (
                <DayCell
                  key={day.valueOf()}
                  type="button"
                  disabled={tooEarly}
                  $selected={Boolean(selected && day.isSame(selected, 'day'))}
                  $today={day.isSame(dayjs(), 'day')}
                  $outside={!day.isSame(viewMonth, 'month')}
                  onClick={() => pickDay(day)}
                >
                  {day.date()}
                </DayCell>
              )
            })}
          </Grid>

          <TimeRow>
            <TimeSelect
              value={selected ? selected.hour() : ''}
              disabled={!selected}
              onChange={(e) => setHour(Number(e.target.value))}
              aria-label={t('marketplace.expiryHour')}
            >
              {!selected && <option value="">--</option>}
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h} disabled={hourDisabled(h)}>
                  {String(h).padStart(2, '0')}
                </option>
              ))}
            </TimeSelect>
            <Text variant={TextVariant.BODY_SMALL}>:</Text>
            <TimeSelect
              value={selected ? selected.minute() : ''}
              disabled={!selected}
              onChange={(e) => setMinute(Number(e.target.value))}
              aria-label={t('marketplace.expiryMinute')}
            >
              {!selected && <option value="">--</option>}
              {MINUTE_STEPS.map((m) => (
                <option key={m} value={m} disabled={minuteDisabled(m)}>
                  {String(m).padStart(2, '0')}
                </option>
              ))}
            </TimeSelect>
          </TimeRow>

          <Flex flexDirection="column" gap="2px">
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('marketplace.expiryEarliest', {
                date: floor.format('D MMM YYYY, HH:mm')
              })}
            </Text>
          </Flex>

          <FooterRow>
            <TextButton
              type="button"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
            >
              {t('marketplace.expiryNever')}
            </TextButton>
            <TextButton type="button" $accent onClick={() => setOpen(false)}>
              {t('marketplace.expiryDone')}
            </TextButton>
          </FooterRow>
        </Panel>
      )}
    </Wrapper>
  )
}

export default ExpiryPicker
